import {useContext, useEffect, useState} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {AxiosError} from 'axios';
import {CareJourneyDetailsResponse, EscalationReason, escalationReasonDisplayValues} from '@/api/dto/care-journey';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useActiveRoster} from '@/store/roster_store';
import {useAuthStore} from '@/store/auth_store';
import {useAlertStore} from '@/store/alert_store';
import {ToastType} from 'patient-ping-remedy/packages/toast';
import Banner, {BannerTypes} from 'patient-ping-remedy/packages/banner';
import {GENERICERROR} from '@manual_encounters/constants/BannerMessages';
import Helpers from '@/helpers/helpers';
import {Section} from '@/components/pages/tasks/task_edit/progress/TaskEditDetails.styles';
import {
  SectionContent,
  SectionHeaderRow,
  SectionTitleRow, StandardSmallTypography,
} from '@/components/pages/tasks/task_edit/progress/CareNoteSection.styles';
import {
  EscalationCancelButton,
  EscalationConfirmationModal,
  EscalationDetailsModal,
  EscalationModalContent,
} from '@/components/pages/tasks/task_edit/progress/EscalationSection.styles';
import {
  DisplayMediumTypography,
  StandardSemiBoldTypography,
  StandardTypography
} from 'patient-ping-remedy/packages/typography';
import Button, {StyleType} from 'patient-ping-remedy/packages/button';
import Icon, {ICONS} from 'patient-ping-remedy/packages/icon';
import DropdownList from 'patient-ping-remedy/packages/dropdown-list';
import {Value} from 'patient-ping-remedy/packages/dropdown-list/lib/DropdownList';
import PermissionsHelpers from '@/helpers/permissions_helpers';
import {Feature, FeatureNew, FeaturePermissionLevel} from '@/api/dto/permissions';
import {useFeatureToggles} from '@/hooks/useFeatureToggles';
import {FeatureToggle} from '@/api/dto/dto';
import {MixpanelEventType, MixpanelTaskEvent} from '@/api/dto/mixpanel';
import {useMixpanel} from '@/hooks/useMixpanel';
import {css} from '@emotion/css';
import {colors, sizes} from 'patient-ping-remedy/packages/theme';
import DisplayHelpers from "@/helpers/display_helpers";
import Loading from '@common/Loading';

type Props = {
  careJourneyDetails: CareJourneyDetailsResponse;
}

const ESCALATION_REASONS = Object.values(EscalationReason);

const centeredModalClass = css({
  '&&': {
    marginTop: 0,
  },
});

export default function EscalationSection({careJourneyDetails}: Props) {
  const {carecoApi} = useContext(CarecoApiContext);
  const currentRoster = useActiveRoster();
  const {currentUser} = useAuthStore();
  const {addAlert} = useAlertStore();
  const queryClient = useQueryClient();
  const {checkBooleanToggle} = useFeatureToggles();
  const {mixpanelEventProperties, getCurrentGroupDetails} = useMixpanel();
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState<EscalationReason | null>(null);

  // Center escalation modals vertically on screen
  useEffect(() => {
    if (showModal || showDetailsModal) {
      const styleEl = document.createElement('style');
      styleEl.setAttribute('data-escalation-modal', 'true');
      styleEl.textContent = '[class*="overlay-container"] { align-items: center !important; }';
      document.head.appendChild(styleEl);
      return () => { styleEl.remove(); };
    }
  }, [showModal, showDetailsModal]);

  const isNewPermissionsEnabled = checkBooleanToggle(FeatureToggle.CARECO_3415_NEW_USER_PERMISSIONS);

  const hasPermission = currentUser && currentRoster && (isNewPermissionsEnabled ?
    PermissionsHelpers.canForUserFeaturePermissionRosterNew(
      currentUser,
      currentRoster,
      FeaturePermissionLevel.ACT,
      FeatureNew.TASKS,
    ) : PermissionsHelpers.canForUserFeaturePermissionRoster(
      currentUser,
      currentRoster,
      FeaturePermissionLevel.ACT,
      Feature.TASKS,
    ));

  const {mutate: escalate, isPending} = useMutation({
    mutationFn: async ({escalated, reason}: {escalated: boolean; reason?: string | null}) =>
      carecoApi?.escalateCareJourney(
        careJourneyDetails.careJourneyInstanceId,
        currentRoster!.id,
        escalated,
        reason,
      ),
    onSuccess(_data, variables) {
      addAlert({
        type: ToastType.SUCCESS,
        content: variables.escalated
          ? 'Care journey marked as escalated to Molina.'
          : 'Care journey escalation removed.',
      });
      queryClient.setQueryData(
        ['careJourneyDetails', careJourneyDetails.careJourneyInstanceId],
        (old?: CareJourneyDetailsResponse) => {
          if (!old) return old;
          return {
            ...old,
            escalatedToCareTeam: variables.escalated,
            escalationReason: variables.reason ?? null,
          };
        }
      );
      void queryClient.invalidateQueries({
        queryKey: ['careJourneyDetails', careJourneyDetails.careJourneyInstanceId],
      });

      if (variables.escalated && mixpanelEventProperties && currentRoster) {
        const event: MixpanelTaskEvent = {
          eventName: MixpanelEventType.BIH_CARE_JOURNEY_ESCALATED,
          eventType: careJourneyDetails.eventType,
          careJourneyName: careJourneyDetails.journeyType,
          ...mixpanelEventProperties,
          ...getCurrentGroupDetails(currentRoster),
        };
        carecoApi?.postMixpanelEvent(event);
      }

      setShowModal(false);
      setSelectedReason(null);
    },
    onError(error: AxiosError) {
      Helpers.alertOnError(
        `Failed to update escalation status. ${GENERICERROR} ${Helpers.traceId(error)}`, error);
    },
  });

  if (!currentRoster?.configuration?.notifyCareTeamEnabled) {
    return null;
  }

  const isEscalated = careJourneyDetails.escalatedToCareTeam;

  const handleConfirmEscalate = () => {
    escalate({escalated: true, reason: selectedReason});
  };


  const modalButtons = [
    <span key="spacer" />,
    <EscalationCancelButton key="cancel" styleType={StyleType.TERTIARY} onClick={() => {
      setShowModal(false);
      setSelectedReason(null);
    }}>
      Cancel
    </EscalationCancelButton>,
    <Button key="confirm" onClick={handleConfirmEscalate} disabled={isPending || !selectedReason}>
      {isPending ? 'Submitting...' : 'Mark as escalated'}
    </Button>,
  ];

  const detailsModalButtons = [
    <span key="spacer" />,
    <Button key="close" styleType={StyleType.PRIMARY} onClick={() => setShowDetailsModal(false)}>
      Close
    </Button>,
  ];

  return (
    <>
      <Section>
        <SectionContent>
          <SectionHeaderRow>
            <div>
              <StandardSemiBoldTypography className={css({display: 'block'})}>
                Escalation
              </StandardSemiBoldTypography>
              <StandardTypography className={css({marginTop: sizes.xSmall})}>
                Mark this care journey as escalated to Molina (Documentation Only)
              </StandardTypography>
            </div>
            <SectionTitleRow>
              {isEscalated ? (
                <div className={css({display: 'flex', alignItems: 'center', gap: '8px'})}>
                  <Icon
                    iconClass={ICONS['check']}
                    weight="far"
                    iconSize="1x"
                    className={css({color: colors.charcoal})}
                  />
                  <StandardTypography>Escalated to Molina</StandardTypography>
                  <button
                    onClick={() => setShowDetailsModal(true)}
                    className={css({
                      background: 'none',
                      border: 'none',
                      borderLeft: `1px solid ${colors.gray3}`,
                      color: colors.purple4,
                      cursor: 'pointer',
                      padding: '0 0 0 8px',
                      fontWeight: 600,
                      fontSize: '14px',
                      textDecoration: 'none',
                      lineHeight: '1.4',
                    })}
                  >
                    View details
                  </button>
                </div>
              ) : (
                <Button
                  onClick={() => setShowModal(true)}
                  styleType={StyleType.SECONDARY}
                  disabled={!hasPermission}
                >
                  Mark as escalated
                </Button>
              )}
            </SectionTitleRow>
          </SectionHeaderRow>
        </SectionContent>
      </Section>

      {showModal && (
        <EscalationConfirmationModal
          className={centeredModalClass}
          headerText={<DisplayMediumTypography>Mark as escalated to Molina</DisplayMediumTypography>}
          hideModal={() => {
            setShowModal(false);
            setSelectedReason(null);
          }}
          buttons={modalButtons}
        >
          <EscalationModalContent>
            <Banner type={BannerTypes.INFORMATIONAL}>
              This action cannot be undone
            </Banner>
            <StandardTypography>
              This only marks the care journey as escalated to Molina in BIH. It does not notify or send the
              escalation to Molina. You must send the escalation to Molina separately via email.
            </StandardTypography>
            <div>
              <StandardSmallTypography className={css({display: 'block'})}>
                Reason for escalation *
              </StandardSmallTypography>
              <DropdownList
                id="escalation-reason"
                items={ESCALATION_REASONS.map(reason => ({
                  label: escalationReasonDisplayValues[reason],
                  value: reason,
                }))}
                value={selectedReason ? {
                  label: escalationReasonDisplayValues[selectedReason],
                  value: selectedReason,
                } : null}
                handleChange={(val: Value | null) => {
                  setSelectedReason((val?.value as EscalationReason) ?? null);
                }}
                placeholder="Select a reason"
              />
            </div>
          </EscalationModalContent>
        </EscalationConfirmationModal>
      )}

      {showDetailsModal && (
        <EscalationDetailsModal
          className={centeredModalClass}
          headerText={<DisplayMediumTypography>Escalation details</DisplayMediumTypography>}
          hideModal={() => setShowDetailsModal(false)}
          buttons={detailsModalButtons}
        >
          <EscalationModalContent>
            {!careJourneyDetails.escalatedBy && !careJourneyDetails.escalatedAt && !careJourneyDetails.escalationReason ? (
              <Loading />
            ) : (
              <>
                <div className={css({display: 'grid', gridTemplateColumns: '1fr 1fr', gap: sizes.medium})}>
                  <div>
                    <StandardSemiBoldTypography className={css({display: 'block', marginBottom: sizes.xxSmall})}>
                      Escalated by
                    </StandardSemiBoldTypography>
                    <StandardTypography>
                      {careJourneyDetails.escalatedBy
                        ? `${careJourneyDetails.escalatedBy.first_name} ${careJourneyDetails.escalatedBy.last_name}`
                        : '—'}
                    </StandardTypography>
                  </div>
                  <div>
                    <StandardSemiBoldTypography className={css({display: 'block', marginBottom: sizes.xxSmall})}>
                      Date and time
                    </StandardSemiBoldTypography>
                    <StandardTypography>
                      {careJourneyDetails.escalatedAt
                        ? DisplayHelpers.formatDateTimeShort(careJourneyDetails.escalatedAt)
                        : '—'}
                    </StandardTypography>
                  </div>
                </div>
                {careJourneyDetails.escalationReason && (
                  <div className={css({marginTop: sizes.medium})}>
                    <StandardSemiBoldTypography className={css({display: 'block', marginBottom: sizes.xxSmall})}>
                      Reason
                    </StandardSemiBoldTypography>
                    <StandardTypography>
                      {escalationReasonDisplayValues[careJourneyDetails.escalationReason] ?? careJourneyDetails.escalationReason}
                    </StandardTypography>
                  </div>
                )}
              </>
            )}
          </EscalationModalContent>
        </EscalationDetailsModal>
      )}
    </>
  );
}
